import React, { useEffect, useState } from "react";
import { useSegment } from "tracking";
import { useAppContext, useLocale } from "hooks";
import { Logger } from "lib/logger";
import { withErrorBoundary } from "react-error-boundary";
import { useUser } from "lib/user";
import type { ReferralRoleType } from "hooks/useReferralRole";
import {
  bannerContainer,
  bannerText
} from "components/Layouts/Banner/SkinnyBanner.css";
import { color } from "lib/theme/theme.css";
import { flexWrapper, hideForDesktop, hideForMobile } from "./RafBanner.css";

type Props = {
  referralRole: Exclude<ReferralRoleType, null>;
};

const RafBannerComponent: React.FC<Props> = ({ referralRole }) => {
  const locale = useLocale();
  const { currencySymbol } = useAppContext();
  const { track } = useSegment();
  const [discountAmount, setDiscountAmount] = useState(10);
  const [thresholdAmount, setThresholdAmount] = useState(60);
  const { name } = useUser();

  const firstName = name?.split(" ")[0] ?? null;

  useEffect(() => {
    setThresholdAmount(locale === "en-GB" ? 60 : 80);
    setDiscountAmount(locale === "en-GB" ? 10 : 20);
  }, [locale]);

  const handleClick = () => {
    track({
      event: "Page Element Clicked",
      properties: {
        module_content: "RAF Banner",
        module_content_url: "/basket"
      }
    });
  };

  const titleText = {
    advocate: `${
      firstName ? `${firstName}, a` : "A"
    } ${currencySymbol}${discountAmount} referral credit is waiting for you at checkout when you spend ${currencySymbol}${thresholdAmount}`,
    friend: `You have been sent ${currencySymbol}${discountAmount} off your first order of ${currencySymbol}${thresholdAmount}`
  };

  return (
    <a
      href="/basket"
      data-testid="RafBanner"
      onClick={handleClick}
      className={bannerContainer}
      style={{ backgroundColor: color.backgroundAccentVariant }}
    >
      <div className={hideForMobile}>
        <div className={flexWrapper}>
          <span
            data-testid="RafBannerDesktopTitleText"
            key="RafBannerDesktopTitleText"
            className={bannerText}
          >
            {titleText[referralRole]}
          </span>
        </div>
      </div>
      <div className={hideForDesktop}>
        <span
          data-testid="RafBannerMobileTitleText"
          key="RafBannerMobileTitleText"
          className={bannerText}
        >
          {titleText[referralRole]}
        </span>
      </div>
    </a>
  );
};

export const RafBanner = withErrorBoundary(RafBannerComponent, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`Raf banner failed to load. ${error}`);
  }
});
